import { HttpStatus, IAnonymousJwtPayload } from '@bloomays-lib/types.shared';
import get from 'lodash/fp/get';

export interface IBloomaysCustomError<T = unknown> {
  extensions: {
    invalidArgs?: unknown[];
    status?: HttpStatus;
    code: string;
    meta?: T;
  };
  cause?: Error;
}

export type BloomaysCustomErrorOptions<T = unknown> = {
  status?: HttpStatus;
  code: string;
  meta?: T;
  invalidArgs?: unknown[];
  cause?: Error;
};

export class BloomaysCustomError<T = unknown> extends Error implements IBloomaysCustomError<T> {
  constructor(message: string, { status, code, meta, invalidArgs, cause }: BloomaysCustomErrorOptions<T>) {
    super(message, { cause });
    this.extensions = {
      status,
      code,
      meta,
      invalidArgs,
    };
    this.name = 'BloomaysCustomError';
    this.status = status;
    // Ensure the error prototype chain works correctly
    Object.setPrototypeOf(this, BloomaysCustomError.prototype);
  }
  extensions: IBloomaysCustomError<T>['extensions'];
  status?: HttpStatus | undefined;
  cause?: Error | undefined;
}

export const getErrorStatusCode = (err: any): number => {
  const statusCode =
    err.status ||
    err.statusCode ||
    get('extensions.status', err) ||
    get('networkError.statusCode', err) ||
    get('extensions.http.status', err);
  return statusCode ? parseInt(statusCode, 10) : 500;
};

export class TokenExpiredError<T extends IAnonymousJwtPayload> extends BloomaysCustomError<T> {
  token: string;
  constructor(message: string, meta: T & { cause?: Error; token: string }) {
    super(message, { code: 'TOKEN_EXPIRED', status: HttpStatus.UNAUTHORIZED, meta });
    Object.defineProperty(this, 'name', { value: 'TokenExpiredError' });
    Object.setPrototypeOf(this, TokenExpiredError.prototype);
    this.token = meta.token;
  }
}

export class AuthenticationError<T = unknown> extends BloomaysCustomError<T> {
  constructor(
    message: string,
    { invalidArgs, status = HttpStatus.UNAUTHORIZED, cause, meta }: Omit<BloomaysCustomErrorOptions<T>, 'code'> = {},
  ) {
    super(message, { cause, invalidArgs, status, code: 'UNAUTHENTICATED', meta });
    Object.defineProperty(this, 'name', { value: 'AuthenticationError' });
    Object.setPrototypeOf(this, AuthenticationError.prototype);
  }
}
